import { Button, Collapse } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { SEO } from '../../components/common/SEO';
import { MarketingLayout } from './MarketingLayout';

/**
 * /pricing - dedicated, prerendered pricing page targeting searches like
 * "school management system pricing Zimbabwe" and "school software cost".
 */
export const PricingPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <MarketingLayout current="/pricing">
      <SEO
        title="School Management System Pricing - $0.50 Per Student | Rongeka Africa"
        description="Transparent school management system pricing for Zimbabwean schools: $0.50 per student per month, one full term free, no setup fees and no contracts. See exactly what your school would pay."
        url="https://rongeka.africa/pricing"
        keywords="school management system pricing, school management software cost Zimbabwe, affordable school software, school ERP price, school administration software pricing"
      />

      <header className="mkt-hero">
        <div className="landing-section-label">PRICING</div>
        <h1 className="mkt-hero-title">One Simple Price: $0.50 Per Student Per Month</h1>
        <p className="mkt-hero-lead">
          No setup fees. No contracts. No per-module licence surprises. Every school gets
          one full term completely free before paying anything.
        </p>
        <div className="mkt-hero-actions">
          <Button type="primary" size="large" onClick={() => navigate('/get-started')}>
            Start Your Free Term
          </Button>
        </div>
      </header>

      <section className="mkt-section">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">What would my school pay?</h2>
          <p className="mkt-section-body">
            You are billed only for active students, only after your free term ends, and only
            if you choose to continue. There is nothing to install and no hardware to buy.
          </p>
          <div className="mkt-grid">
            <div className="mkt-card mkt-price-example">
              <span className="mkt-price-students">A school with 100 students</span>
              <span className="mkt-price-amount">$50</span>
              <span className="mkt-price-period">per month</span>
            </div>
            <div className="mkt-card mkt-price-example">
              <span className="mkt-price-students">A school with 300 students</span>
              <span className="mkt-price-amount">$150</span>
              <span className="mkt-price-period">per month</span>
            </div>
            <div className="mkt-card mkt-price-example">
              <span className="mkt-price-students">A school with 500 students</span>
              <span className="mkt-price-amount">$250</span>
              <span className="mkt-price-period">per month</span>
            </div>
          </div>
        </div>
      </section>

      <section className="mkt-section mkt-section-alt">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">Everything in the core platform</h2>
          <p className="mkt-section-body">
            The $0.50 per student price is not a stripped-down starter tier. It covers the
            complete day-to-day running of a school:
          </p>
          <ul className="mkt-check-list">
            <li><CheckCircleOutlined />Student records, enrollment, and guardian management</li>
            <li><CheckCircleOutlined />Daily attendance registers with WhatsApp parent alerts</li>
            <li><CheckCircleOutlined />Fees, invoicing, arrears tracking, and payment recording</li>
            <li><CheckCircleOutlined />Assessments, grading, results, and report cards</li>
            <li><CheckCircleOutlined />Bulk WhatsApp and email messaging to parents</li>
            <li><CheckCircleOutlined />WhatsApp portal for parents, teachers, students, and drivers</li>
            <li><CheckCircleOutlined />Student ID card generation</li>
            <li><CheckCircleOutlined />Financial reports and analytics</li>
            <li><CheckCircleOutlined />Role-based access for admins, bursars, and teachers</li>
          </ul>
        </div>
      </section>

      <section className="mkt-section">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">Optional add-ons, only if you need them</h2>
          <p className="mkt-section-body">
            Add-on modules extend the system for schools that need more. Each one is activated
            from your settings, billed monthly at its own rate, and can be cancelled any time:
            full double-entry accounting, HR and payroll, library, stock and inventory for
            textbooks and uniforms, fleet and transport, and ECD portfolio media storage.
          </p>
        </div>
      </section>

      <section className="mkt-section mkt-section-alt">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">Pricing questions, answered</h2>
          <Collapse
            className="landing-faq-collapse"
            expandIconPosition="end"
            items={[
              {
                key: '1',
                forceRender: true,
                label: 'When does billing start?',
                children: (
                  <p>
                    Only after your free term ends, and only if you choose to continue. The free
                    trial is one complete school term with no credit card and no demo call
                    required.
                  </p>
                ),
              },
              {
                key: '2',
                forceRender: true,
                label: 'Are there setup fees or contracts?',
                children: (
                  <p>
                    No. There are no setup fees, no onboarding charges, and no lock-in
                    contracts. You can stop using Rongeka at the end of any month.
                  </p>
                ),
              },
              {
                key: '3',
                forceRender: true,
                label: 'Do parents pay anything to use the WhatsApp portal?',
                children: (
                  <p>
                    No. Parents check fee balances, results, and attendance on WhatsApp at no
                    cost beyond their normal WhatsApp usage. The school pays only the per-student
                    platform fee.
                  </p>
                ),
              },
              {
                key: '4',
                forceRender: true,
                label: 'How does Rongeka compare with other school systems in Zimbabwe?',
                children: (
                  <p>
                    Most school management systems in Zimbabwe charge $1 or more per student per
                    month, sell modules as separate licences, or require a paid setup project.
                    Rongeka is $0.50 per student for the whole core platform, with a full free
                    term to evaluate it properly.
                  </p>
                ),
              },
            ]}
          />
          <div className="landing-section-cta-wrap">
            <p className="landing-section-cta-text">
              <ClockCircleOutlined /> One full term free - no demo call, no card.
            </p>
            <Button
              type="primary"
              size="large"
              onClick={() => navigate('/get-started')}
              className="landing-pricing-cta"
            >
              <span>Get Started for Free</span>
              <span className="landing-pricing-cta-arrow">→</span>
            </Button>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
};
